import React, { useMemo } from 'react'
import noop from 'lib/function/noop'
import styled from 'styled-components'
import OfferAirportOptionsGroupedList from '../Airport/OfferAirportOptionsGroupedList'
import { useAppSelector } from 'hooks/reduxHooks'
import NoAirportsOptionListItem from 'components/Flights/FlightsSearch/SearchWidget/FlightAirportSelect/NoAirportsOptionListItem'
import { MELBOURNE_CHARTER_EXCLUDED_AIRPORT_CODES } from 'constants/charterFlight'
import usePopularAirports from 'hooks/Flights/usePopularAirports'

const Wrapper = styled.div`
  position: relative; /* to make the <LoadingIndicator> work  */
`

const hiddenMelbourneAirportCodes = new Set<string>(MELBOURNE_CHARTER_EXCLUDED_AIRPORT_CODES) // MEL charter should not show multi-airport/Avalon

interface Props {
  selectedAirport?: App.Airport
  onSelectAirport?: (airport: App.AirportLocation) => void
  withRadioButtons?: boolean
  searchPhrase?: string
}

function CharterFlightOfferAirportOptionsList(props: Props) {
  const {
    selectedAirport,
    onSelectAirport = noop,
    withRadioButtons,
    searchPhrase = '',
  } = props

  // Get all Australian airports from Redux state
  const australianAirports = useAppSelector(state => state.geo.originAirports)
  const popularDepartureAirports = usePopularAirports('departure')
  const airportRanking = useMemo(() => {
    const ranking = new Map<string, number>()
    popularDepartureAirports.forEach((airport, index) => {
      ranking.set(airport.airportCode, index)
    })
    return ranking
  }, [popularDepartureAirports])

  // Filter to show only Australian airports that are valid for charter departures
  const airports = useMemo(() => {
    return australianAirports
      .filter(airport => airport.countryCode === 'AU' && !hiddenMelbourneAirportCodes.has(airport.code))
      .slice()
      .sort((a, b) => {
        const rankA = airportRanking.get(a.code)
        const rankB = airportRanking.get(b.code)

        if (rankA !== undefined && rankB !== undefined) {
          return rankA - rankB
        }

        if (rankA !== undefined) {
          return -1
        }

        if (rankB !== undefined) {
          return 1
        }

        return a.name.localeCompare(b.name)
      })
  }, [australianAirports, airportRanking])

  const filteredAirports = useMemo(() => {
    const normalizedValue = searchPhrase.trim().toLowerCase()
    if (!normalizedValue) {
      return airports
    }

    return airports.filter(airport => {
      const searchableValue = `${airport.name} ${airport.code}`.toLowerCase()
      return searchableValue.includes(normalizedValue)
    })
  }, [airports, searchPhrase])

  const showNoResults = !!searchPhrase && !filteredAirports.length

  return (
    <Wrapper>
      {showNoResults && <NoAirportsOptionListItem inputValue={searchPhrase} />}
      {!showNoResults && (
        <OfferAirportOptionsGroupedList
          airports={filteredAirports}
          handleItemClick={onSelectAirport}
          selectedAirport={selectedAirport}
          withRadioButtons={withRadioButtons}
        />
      )}
    </Wrapper>
  )
}

export default CharterFlightOfferAirportOptionsList
